import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  RefreshCw,
  Search,
  List,
  AlertCircle,
  Info,
  Check,
  X,
  Settings,
  ShoppingCart,
  CreditCard,
  Building,
  Save,
  Loader2,
  TestTube,
  Truck,
  Plus,
  Trash2,
  Mail,
  Send,
  Upload,
  Image,
  Package,
  Copy,
  ExternalLink,
  Filter,
  MoreVertical,
  ChevronDown
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({ shopify: false, stripe: false, email: false, shipstation: false });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState(null); // ID of type being edited
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [collections, setCollections] = useState([]);
  const [syncedIds, setSyncedIds] = useState(new Set());
  const [fetchingCollections, setFetchingCollections] = useState(false);
  const [newSecrets, setNewSecrets] = useState({}); // Track newly entered secret keys
  const [showManualSyncConfirm, setShowManualSyncConfirm] = useState(false);
  const [syncProgress, setSyncProgress] = useState({ 
    isOpen: false, 
    status: 'idle', // 'syncing', 'success', 'error'
    message: '', 
    details: null 
  });

  const [settings, setSettings] = useState({
    // Shopify
    shopify_store_url: "",
    shopify_api_key: "",
    shopify_api_secret: "",
    shopify_client_id: "",
    shopify_client_secret: "",
    shopify_sync_enabled: false,
    last_shopify_sync: null,
    // Stripe
    stripe_publishable_key: "",
    stripe_secret_key: "",
    stripe_webhook_secret: "",
    stripe_enabled: true,
    // ShipStation
    shipstation_enabled: false,
    shipstation_api_key: "",
    shipstation_api_secret: "",
    shipstation_auto_push: true,
    // Email
    email_enabled: false,
    email_provider: "smtp",
    smtp_host: "smtp.gmail.com",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    email_from_address: "",
    email_from_name: "eDisplayroom",
    admin_notification_email: "",
    infinity_admin_notification_email: "",
    bug_report_emails: "",
    // Platform
    platform_name: "eDisplayroom",
    platform_tagline: "by Infinity Funeral Supply",
    platform_logo_url: "",
    support_email: "",
    support_phone: "",
    funeral_collection_ids: [],
    main_collection_ids: [],
    infinity_collection_ids: [],
    business_address: {
      address: "",
      city: "",
      state: "",
      zipcode: ""
    },
    // Shipping
    shipping_enabled: true,
    free_shipping_threshold: 500,
    shipping_tiers: [
      { min_value: 0, max_value: 100, shipping_cost: 15.00 },
      { min_value: 100, max_value: 250, shipping_cost: 10.00 },
      { min_value: 250, max_value: 500, shipping_cost: 5.00 }
    ],
    // Meta
    updated_at: null,
    updated_by: null
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  const defaultShippingTiers = [
    { min_value: 0, max_value: 100, shipping_cost: 15.00 },
    { min_value: 100, max_value: 250, shipping_cost: 10.00 },
    { min_value: 250, max_value: 500, shipping_cost: 5.00 }
  ];

  const defaultShippingTypes = [
    {
      _id: 'default_standard',
      name: 'Standard Shipping (3-5 Business Days)',
      type: 'Shipping Charge',
      tiers: defaultShippingTiers,
      is_active: true
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setFetchingCollections(true);
      const [settingsRes, shopifyColRes, localColRes, funeralColRes] = await Promise.allSettled([
        axios.get(`${API}/admin/platform-settings`, getAuthHeaders()),
        axios.get(`${API}/admin/collections`, getAuthHeaders()),
        axios.get(`${API}/collections?limit=2000`, getAuthHeaders()),
        axios.get(`${API}/admin/funeral-store/collections?limit=2000`, getAuthHeaders())
      ]);

      // 1. Process Platform Settings (Critical)
      if (settingsRes.status === 'rejected') {
        throw new Error("Critical failure: Could not load platform settings");
      }
      const responseData = settingsRes.value.data;

      // 2. Process Collections (Non-Critical fallback)
      const shopifyCollections = shopifyColRes.status === 'fulfilled'
        ? (shopifyColRes.value.data?.data || shopifyColRes.value.data || [])
        : [];

      const localCollections = localColRes.status === 'fulfilled'
        ? (localColRes.value.data?.data || localColRes.value.data || [])
        : [];

      const funeralCollections = funeralColRes.status === 'fulfilled'
        ? (funeralColRes.value.data?.data || funeralColRes.value.data || [])
        : [];

      if (shopifyColRes.status === 'rejected') {
        console.warn("Shopify collections failed to load. Check credentials.");
        toast.error("Shopify connection failed. Please check your credentials.");
      }

      const funeralIds = funeralCollections
        .filter(c => c.shopify_collection_id)
        .map(c => c.shopify_collection_id);
      const mainIds = localCollections
        .filter(c => c.shopify_collection_id && c.is_main_store !== false)
        .map(c => c.shopify_collection_id);

      // Keep track of which ones are actually in our DB
      const dbSyncedIds = new Set([...funeralIds, ...mainIds].map(id => id ? String(id) : id));
      setSyncedIds(dbSyncedIds);

      // Collections are already filtered/derived from actual database records.
      // We no longer auto-default unsynced collections to Main Store to allow 
      // users to explicitly uncheck them from both stores.
      const initialMainIds = [...mainIds];

      setSettings(prev => ({
        ...prev,
        ...responseData,
        shipping_enabled: responseData.shipping_enabled ?? true,
        free_shipping_threshold: responseData.free_shipping_threshold ?? 500,
        shipping_tiers: responseData.shipping_tiers?.length > 0
          ? responseData.shipping_tiers
          : defaultShippingTiers,
        shipping_types: responseData.shipping_types?.length > 0
          ? responseData.shipping_types
          : defaultShippingTypes,
        funeral_collection_ids: Array.isArray(responseData.funeral_collection_ids) && responseData.funeral_collection_ids.length > 0
          ? responseData.funeral_collection_ids
          : funeralIds,
        main_collection_ids: Array.isArray(responseData.main_collection_ids) && responseData.main_collection_ids.length > 0
          ? responseData.main_collection_ids
          : initialMainIds,
        infinity_collection_ids: Array.isArray(responseData.infinity_collection_ids)
          ? responseData.infinity_collection_ids
          : []
      }));

      setCollections(shopifyCollections);
    } catch (error) {
      toast.error("Failed to load settings or collections");
    } finally {
      setLoading(false);
      setFetchingCollections(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/platform-settings`, settings, getAuthHeaders());
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testShopifyConnection = async () => {
    setTesting(prev => ({ ...prev, shopify: true }));
    try {
      const response = await axios.post(`${API}/admin/test-shopify`, {
        store_url: settings.shopify_store_url,
        client_id: settings.shopify_client_id,
        client_secret: settings.shopify_client_secret
      }, getAuthHeaders());

      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(prev => ({ ...prev, shopify: false }));
    }
  };

  const testStripeConnection = async () => {
    setTesting(prev => ({ ...prev, stripe: true }));
    try {
      const response = await axios.post(`${API}/admin/test-stripe`, {
        secret_key: settings.stripe_secret_key
      }, getAuthHeaders());

      if (response.data.success) {
        toast.success(`${response.data.message}${response.data.business_name ? ` - ${response.data.business_name}` : ''}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTesting(prev => ({ ...prev, stripe: false }));
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, WebP, or SVG.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      formData.append('type', 'platform_logo');

      const response = await axios.post(`${API}/upload/logo`, formData, {
        ...getAuthHeaders()
      });

      if (response.data.url) {
        const newUrl = response.data.url;
        updateSetting('platform_logo_url', newUrl);

        // Auto-save the logo URL to backend immediately
        try {
          await axios.put(`${API}/admin/platform-settings`, { platform_logo_url: newUrl }, getAuthHeaders());
          toast.success('Logo uploaded and saved successfully!');
        } catch (saveError) {
          console.error("Failed to auto-save logo:", saveError);
          toast.warning('Logo uploaded but failed to save. Please click "Save All Settings".');
        }
      }
    } catch (error) {
      toast.error('Failed to upload logo');
      console.error('Upload error:', error);
    } finally {
      setUploadingLogo(false);
    }
  };


  const maskSecret = (value) => {
    if (!value) return "";
    if (value.length <= 10) return "**********";
    // Handle Stripe prefix (sk_test_ or sk_live_) or generic
    const prefix = value.startsWith("sk_") ? (value.startsWith("sk_test_") ? "sk_test_" : (value.startsWith("sk_live_") ? "sk_live_" : "sk_")) : value.substring(0, 4);
    const last4 = value.slice(-4);
    const maskLength = Math.max(value.length - prefix.length - 4, 10);
    return `${prefix}${"*".repeat(maskLength)}${last4}`;
  };

  const channelField = (storeType) =>
    storeType === 'funeral' ? 'funeral_collection_ids'
      : storeType === 'infinity' ? 'infinity_collection_ids'
      : 'main_collection_ids';

  const handleToggleStore = (collectionId, storeType) => {
    setSettings(prev => {
      const field = channelField(storeType);
      const currentIds = [...(prev[field] || [])];

      if (currentIds.includes(collectionId)) {
        return { ...prev, [field]: currentIds.filter(id => id !== collectionId) };
      } else {
        return { ...prev, [field]: [...currentIds, collectionId] };
      }
    });
  };

  const handleSelectAll = (storeType, checked) => {
    const visibleCollections = filteredCollections.filter(c => c.shopify_collection_id);
    const visibleIds = visibleCollections.map(c => c.shopify_collection_id);

    setSettings(prev => {
      const field = channelField(storeType);
      let currentIds = [...(prev[field] || [])];

      if (checked) {
        const combined = [...new Set([...currentIds, ...visibleIds])];
        return { ...prev, [field]: combined };
      } else {
        const remaining = currentIds.filter(id => !visibleIds.includes(id));
        return { ...prev, [field]: remaining };
      }
    });
  };

  const handleSaveMappings = async () => {
    setSaving(true);
    try {
      const mappings = collections.filter(c => c.shopify_collection_id).map(col => {
        const id = col.shopify_collection_id;
        const isMain = settings.main_collection_ids?.includes(id);
        const isFuneral = settings.funeral_collection_ids?.includes(id);
        const isInfinity = settings.infinity_collection_ids?.includes(id);
        return {
          shopify_collection_id: id,
          name: col.name || col.title,
          handle: col.handle,
          is_main: isMain,
          is_funeral: isFuneral,
          is_infinity: isInfinity,
          // Canonical channel names the backend expects ('main' → 'edisplay').
          channels: [isMain && 'edisplay', isFuneral && 'funeral', isInfinity && 'infinity'].filter(Boolean),
        };
      });

      await axios.post(`${API}/admin/collection-mapping`, { mappings }, getAuthHeaders());
      fetchSettings(true);
      toast.success("Collection mappings saved! Products reorganized automatically.");
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    setSyncProgress({
      isOpen: true,
      status: 'syncing',
      message: 'Fetching products from Shopify and updating your catalog. This may take a moment...',
      details: null
    });
    
    try {
      const response = await axios.post(`${API}/admin/sync-shopify`, {}, getAuthHeaders());
      const { edisplay_count, funeral_count, infinity_count } = response.data;

      setSyncProgress({
        isOpen: true,
        status: 'success',
        message: 'Sync Process completed successfully.',
        details: {
          edisplay: edisplay_count || 0,
          funeral: funeral_count || 0,
          infinity: infinity_count || 0
        }
      });
      
      fetchSettings(true);
    } catch (error) {
      console.error("Sync error:", error);
      setSyncProgress({
        isOpen: true,
        status: 'error',
        message: error.response?.data?.detail || 'Sync failed due to a server error or connection issue.',
        details: null
      });
    } finally {
      setSyncing(false);
    }
  };

  const filteredCollections = collections
    .filter(c => {
      const name = c.name || c.title || "Unnamed Collection";
      const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());
      const isFuneral = settings.funeral_collection_ids?.includes(c.shopify_collection_id);
      const isMain = settings.main_collection_ids?.includes(c.shopify_collection_id);
      const isInfinity = settings.infinity_collection_ids?.includes(c.shopify_collection_id);

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'synced' && c.shopify_collection_id) ||
        (statusFilter === 'manual' && !c.shopify_collection_id);

      const matchesStore = storeFilter === 'all' ||
        (storeFilter === 'funeral' && isFuneral) ||
        (storeFilter === 'main' && isMain) ||
        (storeFilter === 'infinity' && isInfinity);

      return matchesSearch && matchesStatus && matchesStore;
    })
    .sort((a, b) => {
      const nameA = (a.name || a.title || "Unnamed Collection").toLowerCase();
      const nameB = (b.name || b.title || "Unnamed Collection").toLowerCase();
      return nameA.localeCompare(nameB);
    });




  return (
    <div className="space-y-6" data-testid="admin-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Platform Settings
          </h1>
          <p className="text-slate-500">Configure integrations and platform details</p>
        </div>

      </div>

      {/* Last Updated */}
      {settings.updated_at && (
        <div className="text-sm text-slate-500 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Last updated: {new Date(settings.updated_at).toLocaleString()} by {settings.updated_by}
        </div>
      )}

      <Tabs defaultValue="shopify" className="space-y-6">
        <TabsList className="grid grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="shopify" className="gap-2">
            <ShoppingCart className="w-4 h-4" />
            Shopify
          </TabsTrigger>
          <TabsTrigger value="stripe" className="gap-2">
            <CreditCard className="w-4 h-4" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="shipstation" className="gap-2">
            <Package className="w-4 h-4" />
            ShipStation
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="shipping" className="gap-2">
            <Truck className="w-4 h-4" />
            Shipping
          </TabsTrigger>
          <TabsTrigger value="platform" className="gap-2">
            <Building className="w-4 h-4" />
            Platform
          </TabsTrigger>
        </TabsList>

        {/* Shopify Tab */}
        <TabsContent value="shopify" className="space-y-6">
          <Tabs defaultValue="collection-sync" className="space-y-6">
            <TabsList className="bg-slate-100/50 p-1 rounded-lg grid grid-cols-2 w-full max-w-xl">
              <TabsTrigger value="collection-sync" className="gap-2">
                <List className="w-4 h-4" />
                Collection Sync Manager
              </TabsTrigger>
              <TabsTrigger value="integration" className="gap-2">
                <Settings className="w-4 h-4" />
                Shopify Integration
              </TabsTrigger>
            </TabsList>

            {/* Sub-tab 1: Collection Sync Manager */}
            <TabsContent value="collection-sync">
              <div className="bg-white rounded-xl shadow-sm border relative overflow-visible">
                <div className="p-6 border-b bg-slate-50/50 sticky top-0 z-30 rounded-t-xl backdrop-blur-md">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
                        <List className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-bold text-slate-900">Shopify Collection Sync Manager</h4>
                          {settings.last_shopify_sync && (
                            <span className="text-[10px] font-bold px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">
                              Last sync: {new Date(settings.last_shopify_sync).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">Map collections to Main or Funeral Supplies</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fetchSettings(true)}
                        disabled={loading}
                        className="gap-2 h-9 border-slate-200"
                      >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh List
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setShowManualSyncConfirm(true)}
                        disabled={syncing}
                        className="gap-2 h-9 bg-primary hover:bg-primary/90 text-white font-semibold shadow-sm"
                      >
                        {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        Sync Products
                      </Button>
                      <Button
                        type="button"
                        onClick={handleSaveMappings}
                        disabled={saving}
                        size="sm"
                        className="gap-2 h-9 bg-indigo-600 hover:bg-indigo-700 font-semibold text-white"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Save Mappings
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search collections..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 h-9 text-sm border-slate-200 rounded-lg"
                      />
                    </div>

                    <select
                      value={storeFilter}
                      onChange={(e) => setStoreFilter(e.target.value)}
                      className="h-9 px-3 py-1 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option value="all">All Stores</option>
                      <option value="main">e-Displayroom Only</option>
                      <option value="funeral">Funeral Supplies Only</option>
                      <option value="infinity">Infinity Touch Only</option>
                    </select>
                  </div>
                </div>
                <div className="max-h-[600px] overflow-y-auto custom-scrollbar border-t">
                  <Table>
                    <TableHeader className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm shadow-sm">
                      <TableRow className="bg-slate-50/30 hover:bg-slate-50/30">
                        <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400 px-6 py-3">Collection Name</TableHead>
                        <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400 px-6 py-3 text-center w-[150px]">
                          <div className="flex items-center justify-center gap-2">
                            <Checkbox
                              checked={filteredCollections.length > 0 && filteredCollections.every(c => settings.main_collection_ids?.includes(c.shopify_collection_id))}
                              onCheckedChange={(checked) => handleSelectAll('main', !!checked)}
                              className="border-indigo-200"
                            />
                            <span>e-Displayroom</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400 px-6 py-3 text-center w-[150px]">
                          <div className="flex items-center justify-center gap-2">
                            <Checkbox
                              checked={filteredCollections.length > 0 && filteredCollections.every(c => settings.funeral_collection_ids?.includes(c.shopify_collection_id))}
                              onCheckedChange={(checked) => handleSelectAll('funeral', !!checked)}
                              className="border-indigo-200"
                            />
                            <span>Funeral Supplies</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400 px-6 py-3 text-center w-[150px]">
                          <div className="flex items-center justify-center gap-2">
                            <Checkbox
                              checked={filteredCollections.length > 0 && filteredCollections.every(c => settings.infinity_collection_ids?.includes(c.shopify_collection_id))}
                              onCheckedChange={(checked) => handleSelectAll('infinity', !!checked)}
                              className="border-indigo-200"
                            />
                            <span>Infinity Touch</span>
                          </div>
                        </TableHead>
                        <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400 px-6 py-3">Sync Status</TableHead>
                        <TableHead className="font-bold text-[11px] uppercase tracking-wider text-slate-400 px-6 py-3">Remarks</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {fetchingCollections ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-24">
                            <div className="flex flex-col items-center gap-3">
                              <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
                              <p className="text-slate-500 font-medium animate-pulse">Loading collections...</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : filteredCollections.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-10 text-slate-500 text-sm italic">
                            No collections found matching your filters
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCollections.map((col) => {
                          const isFuneral = settings.funeral_collection_ids?.includes(col.shopify_collection_id);
                          const isMain = settings.main_collection_ids?.includes(col.shopify_collection_id);
                          const isInfinity = settings.infinity_collection_ids?.includes(col.shopify_collection_id);

                          return (
                            <TableRow key={col._id || col.shopify_collection_id} className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0">
                              <TableCell className="px-6 py-4">
                                <span className="font-medium text-slate-900 text-sm">{col.name || col.title}</span>
                              </TableCell>
                              <TableCell className="px-1 py-4 w-[150px] text-center">
                                <div
                                  className={`flex items-center gap-2 justify-center px-2 py-1 rounded-md cursor-pointer transition-colors ${isMain ? "bg-[#c6efce]/20" : "hover:bg-slate-100"}`}
                                  onClick={() => handleToggleStore(col.shopify_collection_id, 'main')}
                                >
                                  <Checkbox
                                    checked={isMain}
                                    className="border-indigo-200 data-[state=checked]:bg-[#c6efce] data-[state=checked]:text-[#006100] data-[state=checked]:border-[#006100]/20 pointer-events-none"
                                  />
                                  <span className={`text-[11px] font-bold whitespace-nowrap ${isMain ? "text-[#006100]" : "text-slate-400"}`}>e-Displayroom</span>
                                </div>
                              </TableCell>
                              <TableCell className="px-1 py-4 w-[150px] text-center">
                                <div
                                  className={`flex items-center gap-2 justify-center px-2 py-1 rounded-md cursor-pointer transition-colors ${isFuneral ? "bg-[#ffc7ce]/20" : "hover:bg-slate-100"}`}
                                  onClick={() => handleToggleStore(col.shopify_collection_id, 'funeral')}
                                >
                                  <Checkbox
                                    checked={isFuneral}
                                    className="border-indigo-200 data-[state=checked]:bg-[#ffc7ce] data-[state=checked]:text-[#9c0006] data-[state=checked]:border-[#9c0006]/20 pointer-events-none"
                                  />
                                  <span className={`text-[11px] font-bold whitespace-nowrap ${isFuneral ? "text-[#9c0006]" : "text-slate-400"}`}>Funeral Supplies</span>
                                </div>
                              </TableCell>
                              <TableCell className="px-1 py-4 w-[150px] text-center">
                                <div
                                  className={`flex items-center gap-2 justify-center px-2 py-1 rounded-md cursor-pointer transition-colors ${isInfinity ? "bg-[#dbeafe]/40" : "hover:bg-slate-100"}`}
                                  onClick={() => handleToggleStore(col.shopify_collection_id, 'infinity')}
                                >
                                  <Checkbox
                                    checked={isInfinity}
                                    className="border-indigo-200 data-[state=checked]:bg-[#dbeafe] data-[state=checked]:text-[#1e40af] data-[state=checked]:border-[#1e40af]/20 pointer-events-none"
                                  />
                                  <span className={`text-[11px] font-bold whitespace-nowrap ${isInfinity ? "text-[#1e40af]" : "text-slate-400"}`}>Infinity Touch</span>
                                </div>
                              </TableCell>
                              <TableCell className="px-6 py-4">
                                <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${(isMain || isFuneral || isInfinity)
                                  ? "bg-green-50 text-green-700 border border-green-100"
                                  : "bg-orange-50 text-orange-700 border border-orange-100"
                                  }`}>
                                  {(isMain || isFuneral || isInfinity) ? "Synced" : "Not Synced"}
                                </span>
                              </TableCell>

                              <TableCell className="px-6 py-4">
                                {col.is_deleted ? (
                                  <div className="flex flex-col">
                                    <span className="text-[10px] text-red-600 font-bold uppercase tracking-tighter">Will be deleted</span>
                                    <span className="text-[9px] font-mono text-slate-400">Shopify Ref removed</span>
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-slate-500 font-medium">
                                    {[isMain && "e-Displayroom", isFuneral && "Funeral Supplies", isInfinity && "Infinity Touch"].filter(Boolean).join(", ") || "—"}
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>

            {/* Sub-tab 2: Shopify Integration Credentials */}
            <TabsContent value="integration">
              <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <ShoppingCart className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Shopify Integration</h3>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-slate-500">Sync products from your Shopify store</p>
                        {settings.last_shopify_sync && (
                          <>
                            <span className="text-slate-300">•</span>
                            <p className="text-xs font-medium text-primary">
                              Last synced: {new Date(settings.last_shopify_sync).toLocaleString()}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Enable Sync</Label>
                    <Switch
                      checked={settings.shopify_sync_enabled}
                      onCheckedChange={(v) => updateSetting("shopify_sync_enabled", v)}
                    />
                  </div>
                </div>

                {/* Shopify Sync Support Info */}
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Shopify Sync Ready</p>
                    <p className="text-sm text-blue-700">
                      Real Shopify integration is now supported. Enter your store credentials and enable sync to pull your live products. If no credentials are provided, a demo set will be used.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Store URL</Label>
                    <Input
                      value={settings.shopify_store_url || ""}
                      onChange={(e) => updateSetting("shopify_store_url", e.target.value)}
                      placeholder="yourstore.myshopify.com"
                      autoComplete="off"
                    />
                    <p className="text-xs text-slate-500">Your Shopify store domain</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Client ID (API Key)</Label>
                    <Input
                      value={settings.shopify_client_id || ""}
                      onChange={(e) => updateSetting("shopify_client_id", e.target.value)}
                      placeholder="Client ID from Shopify app"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Client Secret (API Secret)</Label>
                    <Input
                      type="password"
                      value={newSecrets["shopify_client_secret"] || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewSecrets(prev => ({ ...prev, shopify_client_secret: val }));
                        if (val) updateSetting("shopify_client_secret", val);
                      }}
                      placeholder={settings.shopify_client_secret ? maskSecret(settings.shopify_client_secret) : "shpss_xxxxx"}
                      className="font-mono text-sm"
                      autoComplete="new-password"
                    />
                    <p className="text-xs text-slate-500">From your Shopify custom app</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={testShopifyConnection}
                    disabled={testing.shopify || !settings.shopify_store_url}
                  >
                    {testing.shopify ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <TestTube className="w-4 h-4 mr-2" />
                    )}
                    Test Connection
                  </Button>
                  <p className="text-sm text-slate-500">
                    Verify your Shopify credentials before saving
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Stripe Tab */}
        <TabsContent value="stripe">
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Stripe Payments</h3>
                  <p className="text-sm text-slate-500">Accept payments from customers</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Enable Payments</Label>
                <Switch
                  checked={settings.stripe_enabled}
                  onCheckedChange={(v) => updateSetting("stripe_enabled", v)}
                />
              </div>
            </div>

            {/* Info Banner */}
            {settings.stripe_publishable_key ? (
              <div className={`p-4 rounded-xl border flex items-start gap-3 ${settings.stripe_publishable_key.startsWith('pk_live_')
                ? 'bg-green-50 border-green-200'
                : 'bg-amber-50 border-amber-200'
                }`}>
                {settings.stripe_publishable_key.startsWith('pk_live_') ? (
                  <Check className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                )}
                <div>
                  <p className={`text-sm font-medium ${settings.stripe_publishable_key.startsWith('pk_live_')
                    ? 'text-green-800'
                    : 'text-amber-800'
                    }`}>
                    {settings.stripe_publishable_key.startsWith('pk_live_') ? 'Live Mode Active' : 'Test Mode Active'}
                  </p>
                  <p className={`text-sm ${settings.stripe_publishable_key.startsWith('pk_live_')
                    ? 'text-green-700'
                    : 'text-amber-700'
                    }`}>
                    {settings.stripe_publishable_key.startsWith('pk_live_')
                      ? 'Your platform is ready to accept real payments.'
                      : 'Using test API key. Replace with live keys when ready to accept real payments.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Payments Not Configured</p>
                  <p className="text-sm text-slate-500">
                    Connect your Stripe account to enable payments on the checkout page.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Publishable Key</Label>
                <Input
                  value={settings.stripe_publishable_key || ""}
                  onChange={(e) => updateSetting("stripe_publishable_key", e.target.value)}
                  placeholder="pk_test_xxxxx or pk_live_xxxxx"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">Used for frontend checkout</p>
              </div>

              <div className="space-y-2">
                <Label>Secret Key</Label>
                <Input
                  type="password"
                  value={newSecrets["stripe_secret_key"] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewSecrets(prev => ({ ...prev, stripe_secret_key: val }));
                    if (val) updateSetting("stripe_secret_key", val);
                  }}
                  placeholder={settings.stripe_secret_key ? maskSecret(settings.stripe_secret_key) : "sk_test_xxxxx"}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">Used for backend API calls</p>
              </div>

              <div className="md:col-span-2">
                <div className="space-y-2">
                  <Label>Webhook Secret (Optional)</Label>
                  <Input
                    type="password"
                    value={newSecrets["stripe_webhook_secret"] || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewSecrets(prev => ({ ...prev, stripe_webhook_secret: val }));
                      if (val) updateSetting("stripe_webhook_secret", val);
                    }}
                    placeholder={settings.stripe_webhook_secret ? maskSecret(settings.stripe_webhook_secret) : "whsec_xxxxx"}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">For verifying webhook signatures</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={testStripeConnection}
                disabled={testing.stripe || !settings.stripe_secret_key}
              >
                {testing.stripe ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4 mr-2" />
                )}
                Test Connection
              </Button>
              <p className="text-sm text-slate-500">
                Verify your Stripe API key is valid
              </p>
            </div>

            {/* Getting Keys Guide */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-2">How to get your Stripe keys:</h4>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                <li>Log in to your <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Dashboard</a></li>
                <li>Go to Developers → API Keys</li>
                <li>Copy your Publishable key and Secret key</li>
                <li>For live payments, toggle to "Live" mode and use those keys</li>
              </ol>
            </div>
          </div>
        </TabsContent>

        {/* ShipStation Tab */}
        <TabsContent value="shipstation">
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">ShipStation Integration</h3>
                  <p className="text-sm text-slate-500">Connect to ShipStation for order fulfillment</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Enable ShipStation</Label>
                <Switch
                  checked={settings.shipstation_enabled}
                  onCheckedChange={(v) => updateSetting("shipstation_enabled", v)}
                />
              </div>
            </div>

            {!settings.shipstation_enabled && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">ShipStation is disabled</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Orders will be tracked locally but not synced to ShipStation. Enable to push orders for fulfillment.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* API Key */}
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  value={newSecrets["shipstation_api_key"] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewSecrets(prev => ({ ...prev, shipstation_api_key: val }));
                    if (val) updateSetting("shipstation_api_key", val);
                  }}
                  placeholder={settings.shipstation_api_key ? maskSecret(settings.shipstation_api_key) : "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"}
                  className="font-mono text-sm"
                />
              </div>

              {/* API Secret */}
              <div className="space-y-2">
                <Label>API Secret</Label>
                <Input
                  type="password"
                  value={newSecrets["shipstation_api_secret"] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewSecrets(prev => ({ ...prev, shipstation_api_secret: val }));
                    if (val) updateSetting("shipstation_api_secret", val);
                  }}
                  placeholder={settings.shipstation_api_secret ? maskSecret(settings.shipstation_api_secret) : "xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
                  className="font-mono text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
              <div className="col-span-full">
                <h4 className="font-medium text-slate-900">Custom Store Credentials (Legacy)</h4>
                <p className="text-sm text-slate-500">Required if using the Custom Store integration method where ShipStation pulls orders.</p>
              </div>

              {/* Custom Store Username */}
              <div className="space-y-2">
                <Label>Username</Label>
                <Input
                  value={settings.shipstation_username || ""}
                  onChange={(e) => updateSetting("shipstation_username", e.target.value)}
                  placeholder="e.g. infinity"
                  className="font-mono text-sm"
                />
              </div>

              {/* Custom Store Password */}
              {/* Custom Store Password */}
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={newSecrets["shipstation_password"] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewSecrets(prev => ({ ...prev, shipstation_password: val }));
                    if (val) updateSetting("shipstation_password", val);
                  }}
                  placeholder={settings.shipstation_password ? maskSecret(settings.shipstation_password) : "Your secure password"}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">Password for ShipStation to connect</p>
              </div>

              {/* Export Page Size */}
              <div className="space-y-2">
                <Label>Export Page Size</Label>
                <Input
                  type="number"
                  min="10"
                  max="500"
                  value={settings.shipstation_export_page_size || 50}
                  onChange={(e) => updateSetting("shipstation_export_page_size", parseInt(e.target.value) || 50)}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">Number of orders per page (Default: 50)</p>
              </div>
            </div>

            {/* Status Mapping */}
            <div className="pt-4 border-t space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-medium text-slate-900">Status Mapping</h4>
                <div className="group relative">
                  <Info className="w-4 h-4 text-slate-400" />
                  <div className="absolute left-1/2 -px-2 py-1 transform -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-72 bg-slate-900 text-white text-xs rounded p-2 z-10 shadow-lg">
                    Map your store's order statuses to ShipStation's expected statuses. Available ShipStation statuses: awaiting_payment, awaiting_shipment, pending_fulfillment, shipped, on_hold, cancelled.
                  </div>
                </div>
              </div>
              <p className="text-sm text-slate-500">Map internal order statuses to ShipStation's format.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 bg-slate-50 p-4 rounded-xl border">
                {[
                  { key: 'pending', label: 'Pending', default: 'awaiting_payment' },
                  { key: 'confirmed', label: 'Confirmed', default: 'awaiting_shipment' },
                  { key: 'processing', label: 'Processing', default: 'pending_fulfillment' },
                  { key: 'shipped', label: 'Shipped', default: 'shipped' },
                  { key: 'delivered', label: 'Delivered', default: 'shipped' },
                  { key: 'cancelled', label: 'Cancelled', default: 'cancelled' },
                  { key: 'failed', label: 'Failed', default: 'cancelled' }
                ].map((status) => (
                  <div key={status.key} className="space-y-1">
                    <Label className="text-xs font-semibold uppercase text-slate-500">{status.label}</Label>
                    <select
                      className="flex h-10 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm ring-offset-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={settings.shipstation_status_mapping?.[status.key] || status.default}
                      onChange={(e) => {
                        const newMapping = {
                          // Merge defaults first in case shipstation_status_mapping is missing or incomplete
                          pending: 'awaiting_payment',
                          confirmed: 'awaiting_shipment',
                          processing: 'pending_fulfillment',
                          shipped: 'shipped',
                          delivered: 'shipped',
                          cancelled: 'cancelled',
                          failed: 'cancelled',
                          ...(settings.shipstation_status_mapping || {}),
                          [status.key]: e.target.value
                        };
                        updateSetting('shipstation_status_mapping', newMapping);
                      }}
                    >
                      <option value="awaiting_payment">Awaiting Payment</option>
                      <option value="awaiting_shipment">Awaiting Shipment</option>
                      <option value="pending_fulfillment">Pending Fulfillment</option>
                      <option value="shipped">Shipped</option>
                      <option value="on_hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-4 border-t">
              <Label>Webhook URL (for ShipStation)</Label>
              <div className="flex gap-2">
                <Input
                  value={`${process.env.REACT_APP_BACKEND_URL}/app/ShipStationApp_prod/order?SS-UserName=${settings.shipstation_username}&SS-Password=${settings.shipstation_password}&action=export&start_date=01/01/2020+00:00&end_date=12/31/2026+23:59&page=1`}
                  readOnly
                  className="font-mono text-sm bg-slate-50"
                />
              </div>
              <p className="text-xs text-slate-500">
                Add this URL in ShipStation → Settings → Integrations → Webhooks to receive tracking updates
              </p>
            </div>

            {/* Auto-push setting */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-900">Auto-push new orders</p>
                <p className="text-sm text-slate-500">Automatically send new paid orders to ShipStation</p>
              </div>
              <Switch
                checked={settings.shipstation_auto_push}
                onCheckedChange={(v) => updateSetting("shipstation_auto_push", v)}
              />
            </div>

            {/* Test Connection */}
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <Button
                variant="outline"
                onClick={async () => {
                  if (!settings.shipstation_api_key || !settings.shipstation_api_secret) {
                    toast.error("Please enter API Key and Secret first");
                    return;
                  }
                  setTesting(prev => ({ ...prev, shipstation: true }));
                  try {
                    const response = await axios.post(`${API}/admin/test-shipstation`, {
                      api_key: settings.shipstation_api_key,
                      api_secret: settings.shipstation_api_secret
                    }, getAuthHeaders());
                    if (response.data.success) {
                      toast.success("ShipStation connection successful!");
                    } else {
                      toast.error(response.data.message || "Connection failed");
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to connect to ShipStation");
                  } finally {
                    setTesting(prev => ({ ...prev, shipstation: false }));
                  }
                }}
                disabled={testing.shipstation || !settings.shipstation_enabled}
                className="gap-2"
              >
                {testing.shipstation ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <TestTube className="w-4 h-4" />
                )}
                Test Connection
              </Button>
              <p className="text-sm text-slate-500">
                Verify your ShipStation credentials are correct
              </p>
            </div>

            {/* Info about ShipStation sync */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="font-medium text-blue-800 mb-2">What syncs with ShipStation:</h4>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li><strong>Orders:</strong> New paid orders are pushed to ShipStation</li>
                <li><strong>Tracking:</strong> Tracking numbers sync back via webhook</li>
                <li><strong>Status:</strong> Shipment status updates automatically</li>
              </ul>
            </div>

            {/* Getting API Keys Guide */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-2">How to get your ShipStation API keys:</h4>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                <li>Log in to your <a href="https://ship.shipstation.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">ShipStation Dashboard <ExternalLink className="w-3 h-3" /></a></li>
                <li>Go to Settings → Account → API Settings</li>
                <li>Generate new API Keys if you haven't already</li>
                <li>Copy the API Key and API Secret</li>
                <li>Set up webhook at Settings → Integrations → Webhooks</li>
              </ol>
            </div>
          </div>
        </TabsContent>

        {/* Email Tab */}
        <TabsContent value="email">
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                  <Mail className="w-6 h-6 text-pink-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Email Configuration (Gmail SMTP)</h3>
                  <p className="text-sm text-slate-500">Configure outgoing emails using Gmail SMTP</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Enable Emails</Label>
                <Switch
                  checked={settings.email_enabled}
                  onCheckedChange={(v) => updateSetting("email_enabled", v)}
                />
              </div>
            </div>

            {!settings.email_enabled && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Emails are disabled</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Welcome emails and notifications will be logged but not sent. Enable emails to send actual emails to resellers.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* SMTP Host */}
              <div className="space-y-2">
                <Label>SMTP Host</Label>
                <Input
                  value={settings.smtp_host}
                  onChange={(e) => updateSetting("smtp_host", e.target.value)}
                  placeholder="smtp.gmail.com"
                />
              </div>

              {/* SMTP Port */}
              <div className="space-y-2">
                <Label>SMTP Port</Label>
                <Input
                  type="number"
                  value={settings.smtp_port}
                  onChange={(e) => updateSetting("smtp_port", parseInt(e.target.value) || 587)}
                  placeholder="587"
                />
                <p className="text-xs text-slate-500">Use 587 for TLS or 465 for SSL</p>
              </div>

              {/* Gmail Email */}
              <div className="space-y-2">
                <Label>Gmail Email Address</Label>
                <Input
                  type="email"
                  value={settings.smtp_username}
                  onChange={(e) => updateSetting("smtp_username", e.target.value)}
                  placeholder="your-email@gmail.com"
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label>Gmail App Password</Label>
                <Input
                  type="password"
                  value={newSecrets["smtp_password"] || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    setNewSecrets(prev => ({ ...prev, smtp_password: val }));
                    if (val) updateSetting("smtp_password", val);
                  }}
                  placeholder={settings.smtp_password ? maskSecret(settings.smtp_password) : "xxxx xxxx xxxx xxxx"}
                  className="font-mono text-sm"
                  autoComplete="new-password"
                />
                <p className="text-xs text-slate-500">Use App Password, not your regular Gmail password</p>
              </div>

              {/* From Name */}
              <div className="space-y-2">
                <Label>From Name</Label>
                <Input
                  value={settings.email_from_name}
                  onChange={(e) => updateSetting("email_from_name", e.target.value)}
                  placeholder="eDisplayroom"
                />
                <p className="text-xs text-slate-500">Name that appears in recipient's inbox</p>
              </div>

              {/* Admin Notification Email */}
              <div className="space-y-2">
                <Label>Admin Notification Email</Label>
                <Input
                  type="email"
                  value={settings.admin_notification_email}
                  onChange={(e) => updateSetting("admin_notification_email", e.target.value)}
                  placeholder="admin@yourdomain.com"
                />
                <p className="text-xs text-slate-500">
                  Receives platform notifications — new reseller applications, new
                  e-Displayroom orders, catalog sync results and contact-form messages
                </p>
              </div>

              {/* Infinity Touch Notification Email */}
              <div className="space-y-2">
                <Label>Infinity Touch Notification Email</Label>
                <Input
                  type="email"
                  value={settings.infinity_admin_notification_email}
                  onChange={(e) => updateSetting("infinity_admin_notification_email", e.target.value)}
                  placeholder="infinitytouchapp@infinityurns.com"
                />
                <p className="text-xs text-slate-500">
                  Receives Infinity Touch events — new case saved, new print order placed
                  and infinitytouch.app contact-form messages. Falls back to the admin
                  notification email when left blank.
                </p>
              </div>

              {/* Bug Report Emails */}
              <div className="space-y-2">
                <Label>Bug Report Emails</Label>
                <Input
                  type="text"
                  value={settings.bug_report_emails}
                  onChange={(e) => updateSetting("bug_report_emails", e.target.value)}
                  placeholder="abhisek.pal@codomotive.org, someone@else.com"
                />
                <p className="text-xs text-slate-500">
                  Receives bug reports submitted from the Infinity Touch app's "Report a
                  bug" screen. Separate multiple addresses with commas. Reports are emailed
                  only — they are not stored — so this falls back to the Infinity Touch
                  notification email when left blank.
                </p>
              </div>
            </div>

            {/* Test Email */}
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <Button
                variant="outline"
                onClick={async () => {
                  if (!settings.smtp_username || !settings.smtp_password) {
                    toast.error("Please configure Gmail email and App Password first");
                    return;
                  }
                  setTesting(prev => ({ ...prev, email: true }));
                  try {
                    const response = await axios.post(`${API}/admin/test-email`, {
                      to_email: settings.admin_notification_email || settings.smtp_username
                    }, getAuthHeaders());
                    if (response.data.success) {
                      toast.success("Test email sent successfully!");
                    } else {
                      toast.error(response.data.message || "Failed to send test email");
                    }
                  } catch (error) {
                    toast.error(error.response?.data?.detail || "Failed to send test email");
                  } finally {
                    setTesting(prev => ({ ...prev, email: false }));
                  }
                }}
                disabled={testing.email || !settings.email_enabled}
                className="gap-2"
              >
                {testing.email ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Send Test Email
              </Button>
              <p className="text-sm text-slate-500">
                Send a test email to verify your configuration
              </p>
            </div>

            {/* Email Types Info */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <h4 className="font-medium text-blue-800 mb-2">Emails sent by the system:</h4>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li><strong>Welcome Email:</strong> Sent to new resellers when their application is approved</li>
                <li><strong>Admin Notification:</strong> Sent to admin when a new reseller applies</li>
                <li><strong>Order Confirmation:</strong> Sent to customers after successful checkout</li>
              </ul>
            </div>

            {/* Gmail App Password Guide */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-2">How to get a Gmail App Password:</h4>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                <li>Go to your <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Account Security</a></li>
                <li>Enable 2-Step Verification if not already enabled</li>
                <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">App Passwords</a></li>
                <li>Select "Mail" and your device, then click "Generate"</li>
                <li>Copy the 16-character password and paste it above</li>
              </ol>
              <p className="text-xs text-amber-600 mt-3">
                ⚠️ Never use your regular Gmail password. Always use an App Password for security.
              </p>
            </div>
          </div>
        </TabsContent>

        {/* Shipping Tab */}
        <TabsContent value="shipping">
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Truck className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Shipping Configuration</h3>
                  <p className="text-sm text-slate-500">Manage shipping methods and rates</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Enable Shipping</Label>
                <Switch
                  checked={settings.shipping_enabled}
                  onCheckedChange={(v) => updateSetting("shipping_enabled", v)}
                />
              </div>
            </div>

            {/* Global Free Shipping Threshold */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-emerald-800">Global Free Shipping Threshold</h4>
                    <p className="text-sm text-emerald-700">Orders above this amount get free shipping (applies to all types)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-emerald-700 font-medium">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settings.free_shipping_threshold || 500}
                    onChange={(e) => updateSetting("free_shipping_threshold", parseFloat(e.target.value) || 0)}
                    className="w-28 text-right header-input"
                  />
                </div>
              </div>
            </div>

            {/* Content Switch: List vs Detail */}
            {editingTypeId === null ? (
              /* ================== LIST VIEW ================== */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-slate-900">Shipping Types</h4>
                  <Button
                    onClick={() => {
                      const newType = {
                        _id: `new_${Date.now()}`,
                        name: "New Shipping Type",
                        type: "Shipping Charge",
                        tiers: [],
                        is_active: true
                      };
                      const newTypes = [...(settings.shipping_types || []), newType];
                      updateSetting("shipping_types", newTypes);
                      setEditingTypeId(newType._id);
                    }}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" /> Add Shipping Type
                  </Button>
                </div>

                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 font-medium text-slate-600">Name</th>
                        <th className="px-4 py-3 font-medium text-slate-600">Type</th>
                        <th className="px-4 py-3 font-medium text-slate-600">Tiers</th>
                        <th className="px-4 py-3 font-medium text-slate-600 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(settings.shipping_types || []).map((type) => (
                        <tr key={type._id} className="hover:bg-slate-50/50">
                          <td className="px-4 py-3 font-medium">{type.name}</td>
                          <td className="px-4 py-3 text-slate-500">{type.type}</td>
                          <td className="px-4 py-3 text-slate-500">{type.tiers?.length || 0} tiers</td>
                          <td className="px-4 py-3 text-right space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditingTypeId(type._id)}
                            >
                              Edit Tiers
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => {
                                if (confirm('Are you sure you want to delete this shipping type?')) {
                                  updateSetting("shipping_types", settings.shipping_types.filter(t => t._id !== type._id));
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {(settings.shipping_types || []).length === 0 && (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-slate-500">
                            No shipping types defined. Add one to get started.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* ================== DETAIL VIEW ================== */
              (() => {
                const typeIndex = (settings.shipping_types || []).findIndex(t => t._id === editingTypeId);
                const currentType = settings.shipping_types?.[typeIndex];

                if (!currentType) {
                  setEditingTypeId(null);
                  return null;
                }

                const updateCurrentType = (updates) => {
                  const newTypes = [...settings.shipping_types];
                  newTypes[typeIndex] = { ...newTypes[typeIndex], ...updates };
                  updateSetting("shipping_types", newTypes);
                };

                return (
                  <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-200">
                    <div className="flex items-center gap-4 border-b pb-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingTypeId(null)}
                        className="gap-2"
                      >
                        ← Back
                      </Button>
                      <h4 className="font-semibold text-lg">Edit {currentType.name}</h4>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label>Type Name</Label>
                        <Input
                          value={currentType.name}
                          onChange={(e) => updateCurrentType({ name: e.target.value })}
                          placeholder="e.g., Standard Shipping"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Charge Structure</Label>
                        <div className="p-2 border rounded-md bg-slate-50 text-sm text-slate-600">
                          {currentType.type || "Shipping Charge"}
                        </div>
                      </div>
                    </div>

                    {/* Tiers Management */}
                    <div className="space-y-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-slate-900">Price Range Tiers</h4>
                          <p className="text-sm text-slate-500">Define costs based on cart subtotal</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const tiers = currentType.tiers || [];
                            const lastTier = tiers[tiers.length - 1];
                            const newMinValue = lastTier ? lastTier.max_value : 0;
                            updateCurrentType({
                              tiers: [...tiers, { min_value: newMinValue, max_value: newMinValue + 100, shipping_cost: 5.00 }]
                            });
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Tier
                        </Button>
                      </div>

                      {/* Tier Headers */}
                      <div className="grid grid-cols-4 gap-4 px-4 text-sm font-medium text-slate-600">
                        <div>Min Cart Value</div>
                        <div>Max Cart Value</div>
                        <div>Shipping Cost</div>
                        <div></div>
                      </div>

                      {/* Tier Rows */}
                      <div className="space-y-3">
                        {(currentType.tiers || []).map((tier, index) => (
                          <div key={index} className="grid grid-cols-4 gap-4 items-center p-4 bg-slate-50 rounded-xl">
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">$</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={tier.min_value}
                                onChange={(e) => {
                                  const newTiers = [...currentType.tiers];
                                  newTiers[index] = { ...newTiers[index], min_value: parseFloat(e.target.value) || 0 };
                                  updateCurrentType({ tiers: newTiers });
                                }}
                                className="text-right"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">$</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={tier.max_value}
                                onChange={(e) => {
                                  const newTiers = [...currentType.tiers];
                                  newTiers[index] = { ...newTiers[index], max_value: parseFloat(e.target.value) || 0 };
                                  updateCurrentType({ tiers: newTiers });
                                }}
                                className="text-right"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-slate-500">$</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={tier.shipping_cost}
                                onChange={(e) => {
                                  const newTiers = [...currentType.tiers];
                                  newTiers[index] = { ...newTiers[index], shipping_cost: parseFloat(e.target.value) || 0 };
                                  updateCurrentType({ tiers: newTiers });
                                }}
                                className="text-right"
                              />
                            </div>
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  const newTiers = currentType.tiers.filter((_, i) => i !== index);
                                  updateCurrentType({ tiers: newTiers });
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {(currentType.tiers || []).length === 0 && (
                          <div className="text-center py-4 text-slate-500 italic bg-slate-50 rounded-xl border border-dashed">
                            No price tiers defined. Add a tier to set shipping costs.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()
            )}

            {/* Info about shipping calculation */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">How shipping is calculated</p>
                <p className="text-sm text-blue-700 mt-1">
                  Shipping cost is determined by the specific shipping method selected at checkout. If the cart total exceeds the <strong>Global Free Shipping Threshold</strong> (${settings.free_shipping_threshold || 500}), shipping is free regardless of the method (unless configured otherwise in specific logic).
                  <br />
                  Ensure your tiers cover all possible cart values (e.g., 0-100, 100-500) to avoid "Shipping Unavailable" errors.
                </p>
              </div>
            </div>

          </div>
        </TabsContent>

        {/* Platform Tab */}
        <TabsContent value="platform">
          <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Building className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">Platform Information</h3>
                <p className="text-sm text-slate-500">Basic platform details, branding, and contact info</p>
              </div>
            </div>

            {/* Platform Logo Section */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <Label className="mb-3 block font-medium">Platform Logo</Label>
              <div className="flex items-start gap-6">
                {/* Logo Preview */}
                <div className="flex-shrink-0">
                  {settings.platform_logo_url ? (
                    <div className="relative group">
                      <img
                        src={getImageUrl(settings.platform_logo_url)}
                        alt="Platform Logo"
                        className="w-32 h-32 object-contain border rounded-xl bg-white p-2"
                      />
                      <button
                        onClick={() => updateSetting('platform_logo_url', '')}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-slate-300 rounded-xl flex items-center justify-center bg-white">
                      <Image className="w-10 h-10 text-slate-300" />
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-3">
                  <div className="flex gap-3">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        disabled={uploadingLogo}
                      />
                      <span className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${uploadingLogo
                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                        : 'bg-white hover:bg-slate-50 text-slate-700 cursor-pointer'
                        }`}>
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </span>
                    </label>
                  </div>

                  <p className="text-xs text-slate-500">
                    Recommended: PNG or SVG with transparent background
                    <br />
                    Maximum file size: 5MB
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Platform Name</Label>
                <Input
                  value={settings.platform_name || ""}
                  onChange={(e) => updateSetting("platform_name", e.target.value)}
                  placeholder="eDisplayroom"
                />
              </div>

              <div className="space-y-2">
                <Label>Tagline</Label>
                <Input
                  value={settings.platform_tagline || ""}
                  onChange={(e) => updateSetting("platform_tagline", e.target.value)}
                  placeholder="by Infinity Funeral Supply"
                />
              </div>

              <div className="space-y-2">
                <Label>Support Email</Label>
                <Input
                  type="email"
                  value={settings.support_email || ""}
                  onChange={(e) => updateSetting("support_email", e.target.value)}
                  placeholder="support@edisplayroom.com"
                />
              </div>

              <div className="space-y-2">
                <Label>Support Phone</Label>
                <Input
                  value={settings.support_phone || ""}
                  onChange={(e) => updateSetting("support_phone", e.target.value)}
                  placeholder="1-800-XXX-XXXX"
                />
              </div>

              {/* Business Address Section */}
              <div className="col-span-full pt-4 border-t">
                <h4 className="font-medium text-slate-900 mb-4">Business Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-full space-y-2">
                    <Label>Street Address</Label>
                    <Input
                      value={settings.business_address?.address || ""}
                      onChange={(e) => updateSetting("business_address", { ...settings.business_address, address: e.target.value })}
                      placeholder="123 Infinity Way"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input
                      value={settings.business_address?.city || ""}
                      onChange={(e) => updateSetting("business_address", { ...settings.business_address, city: e.target.value })}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>State</Label>
                    <Input
                      value={settings.business_address?.state || ""}
                      onChange={(e) => updateSetting("business_address", { ...settings.business_address, state: e.target.value })}
                      placeholder="State"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zip Code</Label>
                    <Input
                      value={settings.business_address?.zipcode || ""}
                      onChange={(e) => updateSetting("business_address", { ...settings.business_address, zipcode: e.target.value })}
                      placeholder="Zip Code"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Info about where logo is used */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Where the logo appears:</p>
                <ul className="text-sm text-blue-700 mt-1 list-disc list-inside">
                  <li>Landing page header</li>
                  <li>Admin dashboard</li>
                  <li>Reseller dashboard</li>
                  <li>Email templates</li>
                </ul>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Save Button (Fixed) */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Settings
        </Button>
      </div>

      {/* Manual Sync Confirmation Dialog */}
      <AlertDialog open={showManualSyncConfirm} onOpenChange={setShowManualSyncConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-indigo-600" />
              </div>
              <AlertDialogTitle className="text-lg">Start Shopify Sync?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-slate-500">
              This will start syncing all the products from shopify, it might take 2-5 minutes
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-indigo-600 hover:bg-indigo-700 gap-2"
              onClick={() => {
                setShowManualSyncConfirm(false);
                handleSyncNow();
              }}
            >
              <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              Yes start syning
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
            
      {/* Shopify Sync Progress Modal */}
      <Dialog open={syncProgress.isOpen} onOpenChange={(open) => {
        if (syncProgress.status !== 'syncing') {
          setSyncProgress(prev => ({ ...prev, isOpen: open }));
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {syncProgress.status === 'syncing' ? (
                <>
                  <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
                  Syncing Catalog...
                </>
              ) : syncProgress.status === 'success' ? (
                <>
                  <Check className="w-5 h-5 text-green-600" />
                  Sync Successful
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  Sync Failed
                </>
              )}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {syncProgress.message}
            </DialogDescription>
          </DialogHeader>

          {syncProgress.status === 'syncing' && (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-indigo-100 rounded-full"></div>
                <div className="absolute top-0 w-16 h-16 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <p className="text-sm font-medium text-slate-600 animate-pulse">Communicating with Shopify...</p>
            </div>
          )}

          {syncProgress.status === 'success' && syncProgress.details && (
            <div className="bg-slate-50 border rounded-xl p-4 space-y-3 mt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">e-Displayroom</span>
                <span className="font-bold text-slate-900">{syncProgress.details.edisplay} products</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Funeral Store</span>
                <span className="font-bold text-slate-900">{syncProgress.details.funeral} products</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Infinity Touch</span>
                <span className="font-bold text-slate-900">{syncProgress.details.infinity} products</span>
              </div>
              <div className="pt-2 border-t flex items-center gap-2 text-xs text-slate-400 italic">
                <Info className="w-3 h-3" />
                Database has been updated with live Shopify data.
              </div>
            </div>
          )}

          {syncProgress.status === 'error' && (
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 mt-2">
              <div className="flex items-start gap-3">
                <X className="w-4 h-4 text-red-600 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{syncProgress.message}</p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button 
              className={syncProgress.status === 'syncing' ? 'hidden' : 'w-full'}
              onClick={() => setSyncProgress(prev => ({ ...prev, isOpen: false }))}
              variant={syncProgress.status === 'error' ? 'destructive' : 'default'}
            >
              {syncProgress.status === 'error' ? 'Close and Check Credentials' : 'Great, close this!'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
